/**
 * Per-engine error-signature tables for `classifyDbError`.
 *
 * Each signature maps a stable driver/server message phrase (or code) to a
 * low-cardinality `AnalyticsErrorCategory`, optionally pinning a canonical
 * driver `code` for messages whose structured code was lost at the IPC
 * boundary. Tables are ordered most-specific-first; the first match wins.
 *
 * Message fixtures are sourced from the real drivers (tedious/mssql, pg,
 * mysql2, oracledb, better-sqlite3) — see dbErrorClassifier.test.ts.
 * NEVER add a signature that would need raw user data to match.
 */
import type { AnalyticsErrorCategory } from './analyticsTypes';

export type DbErrorSignature = {
  match: RegExp;
  category: AnalyticsErrorCategory;
  /** Canonical driver code to report when the message carries none. */
  code?: string;
};

/** Node/socket-level failures shared by every network engine. */
export const SHARED_SIGNATURES: DbErrorSignature[] = [
  // SSH tunnel leg (formatSshTunnelError wording, electron/sshTunnelErrors.ts)
  { match: /SSH (rejected|authentication)/i, category: 'ssh_tunnel' },
  { match: /SSH (host key|agent)|bastion|`ssh /i, category: 'ssh_tunnel' },
  { match: /private key file|passphrase is incorrect/i, category: 'ssh_tunnel' },
  // TLS: server demands encryption the client didn't offer
  { match: /server requires encryption/i, category: 'tls_required', code: 'EENCRYPT' },
  // TLS: client demanded encryption the server can't do (pg wording)
  { match: /server does not support SSL/i, category: 'tls_unsupported' },
  // DNS
  { match: /getaddrinfo|ENOTFOUND|EAI_AGAIN|host not found/i, category: 'dns_not_found' },
  // TCP
  { match: /ECONNREFUSED|connection refused/i, category: 'conn_refused' },
  { match: /EHOSTUNREACH|ENETUNREACH|EHOSTDOWN|unreachable/i, category: 'host_unreachable' },
  { match: /ETIMEDOUT|ETIMEOUT|timed? ?out/i, category: 'timeout' },
  { match: /ECONNRESET|EPIPE|socket hang up|connection terminated unexpectedly/i, category: 'connection_lost' },
  // App/driver-manager wording for a dead session (incl. the renderer's
  // "Connection isn't active" banner copy, the engine managers' colon-less
  // "Connection not found or disconnected", and the mock engine's default)
  { match: /connection lost|connection (is|was) closed|connection isn't active|connection not found( or disconnected|:)|no connection available/i, category: 'connection_lost' },
  // OS blocked the socket (macOS Local Network permission, firewalls)
  { match: /\bEPERM\b|\bEACCES\b/, category: 'permission' },
];

const MSSQL_SIGNATURES: DbErrorSignature[] = [
  // Azure firewall message also contains "Cannot open server" — keep first.
  { match: /Cannot open server .* (is not allowed to access|firewall rule)/is, category: 'firewall_blocked', code: '40615' },
  { match: /Cannot open database/i, category: 'db_not_found', code: '4060' },
  { match: /Login failed for user|\bELOGIN\b|Login failed\./i, category: 'auth_failed', code: 'ELOGIN' },
  // SQL Network Interfaces error 26 / tedious instance lookup (EINSTLOOKUP)
  { match: /error locating server\/instance specified|SQL Network Interfaces, error: 26/i, category: 'instance_not_found' },
  { match: /\bEINSTLOOKUP\b|SQL Server Browser|Port for \S+ not found/i, category: 'instance_not_found', code: 'EINSTLOOKUP' },
  // tedious connect timeout: "Failed to connect to host:1433 in 15000ms"
  { match: /Failed to connect to .{1,200} in \d+ ?ms/i, category: 'timeout', code: 'ETIMEOUT' },
  // tedious socket-level wrapper with no recognisable cause ("(sequence)" =
  // sequential connector, "(parallel)" = Happy-Eyeballs connector.js path)
  { match: /Could not connect \((sequence|parallel)\)/i, category: 'network', code: 'ESOCKET' },
  // Query-side signatures (server messages are stable English)
  { match: /Incorrect syntax near|Must declare the scalar variable|Unclosed quotation mark/i, category: 'syntax_error', code: '102' },
  { match: /Invalid object name/i, category: 'object_not_found', code: '208' },
  { match: /Invalid column name/i, category: 'object_not_found', code: '207' },
  { match: /Could not find stored procedure/i, category: 'object_not_found', code: '2812' },
  { match: /Violation of (PRIMARY KEY|UNIQUE KEY) constraint|Cannot insert duplicate key/i, category: 'constraint_violation', code: '2627' },
  { match: /conflicted with the (FOREIGN KEY|REFERENCE|CHECK) constraint/i, category: 'constraint_violation', code: '547' },
  { match: /Cannot insert the value NULL into column/i, category: 'constraint_violation', code: '515' },
  { match: /Conversion failed when converting|Error converting data type|Arithmetic overflow/i, category: 'conversion_error' },
  { match: /deadlocked on lock resources|Lock request time out/i, category: 'lock_contention', code: '1205' },
  { match: /permission was denied|does not have permission/i, category: 'permission' },
];

const PGSQL_SIGNATURES: DbErrorSignature[] = [
  { match: /password authentication failed/i, category: 'auth_failed', code: '28P01' },
  // pg_hba rejecting a non-SSL client means the server requires TLS
  { match: /no pg_hba\.conf entry .*(no encryption|SSL off)/is, category: 'tls_required', code: '28000' },
  { match: /no pg_hba\.conf entry/i, category: 'auth_failed', code: '28000' },
  { match: /role .* does not exist/i, category: 'auth_failed', code: '28000' },
  { match: /database .* does not exist/i, category: 'db_not_found', code: '3D000' },
  { match: /Connection terminated due to connection timeout|timeout expired/i, category: 'timeout' },
  // Query-side
  { match: /syntax error at or near|syntax error at end of input/i, category: 'syntax_error', code: '42601' },
  { match: /relation .* does not exist/i, category: 'object_not_found', code: '42P01' },
  { match: /column .* does not exist/i, category: 'object_not_found', code: '42703' },
  { match: /function .* does not exist/i, category: 'object_not_found', code: '42883' },
  { match: /duplicate key value violates unique constraint/i, category: 'constraint_violation', code: '23505' },
  { match: /violates foreign key constraint/i, category: 'constraint_violation', code: '23503' },
  { match: /violates not-null constraint/i, category: 'constraint_violation', code: '23502' },
  { match: /violates check constraint/i, category: 'constraint_violation', code: '23514' },
  { match: /invalid input syntax for|cannot cast type/i, category: 'conversion_error', code: '22P02' },
  { match: /permission denied for/i, category: 'permission', code: '42501' },
  { match: /canceling statement due to statement timeout/i, category: 'timeout', code: '57014' },
  { match: /canceling statement due to user request/i, category: 'cancelled', code: '57014' },
  { match: /deadlock detected/i, category: 'lock_contention', code: '40P01' },
];

const MYSQL_SIGNATURES: DbErrorSignature[] = [
  { match: /Access denied for user/i, category: 'auth_failed', code: '1045' },
  { match: /Client does not support authentication protocol/i, category: 'auth_failed', code: '1251' },
  { match: /Connections using insecure transport are prohibited/i, category: 'tls_required', code: '3159' },
  // mysql2 client_handshake.js wording when the client asked for TLS the
  // server can't do (the shared "does not support SSL" phrase is pg's).
  { match: /Server does not support secure connection/i, category: 'tls_unsupported', code: 'HANDSHAKE_NO_SSL_SUPPORT' },
  { match: /Unknown database/i, category: 'db_not_found', code: '1049' },
  // Query-side
  { match: /error in your SQL syntax/i, category: 'syntax_error', code: '1064' },
  { match: /Table .* doesn't exist/i, category: 'object_not_found', code: '1146' },
  { match: /Unknown column/i, category: 'object_not_found', code: '1054' },
  { match: /Duplicate entry/i, category: 'constraint_violation', code: '1062' },
  { match: /a foreign key constraint fails/i, category: 'constraint_violation', code: '1452' },
  { match: /cannot be null/i, category: 'constraint_violation', code: '1048' },
  { match: /Incorrect (integer|datetime|date|decimal|double) value/i, category: 'conversion_error', code: '1366' },
  { match: /command denied to user/i, category: 'permission', code: '1142' },
  { match: /Lock wait timeout exceeded/i, category: 'lock_contention', code: '1205' },
  { match: /Deadlock found when trying to get lock/i, category: 'lock_contention', code: '1213' },
  { match: /Query execution was interrupted/i, category: 'cancelled', code: '1317' },
];

/** Oracle: ORA-/NJS- codes are always embedded in the message text. */
const ORACLE_CODE_CATEGORIES: Record<string, AnalyticsErrorCategory> = {
  'ORA-01017': 'auth_failed',
  'ORA-28000': 'auth_failed', // account locked
  'ORA-28001': 'auth_failed', // password expired
  'ORA-28002': 'auth_failed', // password will expire (grace period)
  'ORA-12541': 'conn_refused', // TNS no listener
  'NJS-511': 'conn_refused',
  'ORA-12514': 'instance_not_found', // service not known to listener
  'ORA-12505': 'instance_not_found', // SID not known to listener
  'NJS-518': 'instance_not_found', // thin driver's own "service/SID not registered with listener"
  'NJS-138': 'unsupported_server', // server predates 12.1 — thin mode can't talk to it, no client-side fix
  'ORA-12154': 'dns_not_found', // could not resolve connect identifier
  'ORA-12170': 'timeout',
  'NJS-510': 'timeout',
  'ORA-00900': 'syntax_error',
  'ORA-00907': 'syntax_error',
  'ORA-00933': 'syntax_error',
  'ORA-00936': 'syntax_error',
  'ORA-00942': 'object_not_found', // table or view does not exist
  'ORA-00904': 'object_not_found', // invalid identifier
  'ORA-00001': 'constraint_violation', // unique constraint
  'ORA-02291': 'constraint_violation', // FK parent not found
  'ORA-01400': 'constraint_violation', // cannot insert NULL
  'ORA-01722': 'conversion_error', // invalid number
  'ORA-01861': 'conversion_error', // literal/format mismatch
  'ORA-01031': 'permission',
  'ORA-00060': 'lock_contention', // deadlock
  'ORA-01013': 'cancelled', // user requested cancel
  'ORA-03113': 'connection_lost', // end-of-file on communication channel
  'ORA-06550': 'syntax_error', // generic PL/SQL compilation-error wrapper
  // Jam's own Oracle lease gate codes (electron/oracle-lease/oracleLeaseErrors.ts):
  // the pool was condemned and rebuilt under the caller — the session is gone,
  // the settings are fine.
  JAM_ORACLE_POOL_TAINTED: 'connection_lost',
  JAM_ORACLE_POOL_CLOSED: 'connection_lost',
};

const SQLITE_SIGNATURES: DbErrorSignature[] = [
  { match: /unable to open database file/i, category: 'file_not_found', code: 'SQLITE_CANTOPEN' },
  { match: /file is not a database/i, category: 'file_not_found', code: 'SQLITE_NOTADB' },
  { match: /syntax error/i, category: 'syntax_error', code: 'SQLITE_ERROR' },
  { match: /no such (table|column|function|view|index)/i, category: 'object_not_found', code: 'SQLITE_ERROR' },
  { match: /(UNIQUE|FOREIGN KEY|NOT NULL|CHECK) constraint failed/i, category: 'constraint_violation', code: 'SQLITE_CONSTRAINT' },
  { match: /database is locked/i, category: 'lock_contention', code: 'SQLITE_BUSY' },
  { match: /attempt to write a readonly database/i, category: 'permission', code: 'SQLITE_READONLY' },
];

const ENGINE_SIGNATURES: Record<string, DbErrorSignature[]> = {
  mssql: MSSQL_SIGNATURES,
  pgsql: PGSQL_SIGNATURES,
  mysql: MYSQL_SIGNATURES,
  sqlite: SQLITE_SIGNATURES,
};

export function signaturesForEngine(engine: string | undefined): DbErrorSignature[] {
  return (engine && ENGINE_SIGNATURES[engine]) || [];
}

/** Category for an Oracle ORA-/NJS- code (from message text or structured code) or a JAM_ORACLE_* lease code. */
export function oracleCodeCategory(code: string): AnalyticsErrorCategory | undefined {
  return ORACLE_CODE_CATEGORIES[code];
}
